import { createAsyncThunk } from "@reduxjs/toolkit"

import { errorSave } from "../../../redux/errors.slice"
import { unwrapEnvelope } from "../../../utils/api/unwrap-envelope.ts"
import { unknownError } from "../../../utils/handlers/handle-error"
import * as services from "../api/invoices.services.ts"
import type { InvoicesListParams } from "../api/invoices.services.ts"
import type {
  BillableLinesResponse,
  BillableLinesResult,
  GenerateInvoicePayload,
  InvoiceDelivery,
  InvoiceListItem,
  InvoicesListResponse,
  SendInvoiceResponse
} from "../types/invoices.types.ts"

export const sliceName = "invoices"

export const fetchInvoicesList = createAsyncThunk<
  InvoicesListResponse,
  InvoicesListParams & { isMore?: boolean }
>(
  `${sliceName}/fetchInvoicesList`,
  // `isMore` tells the reducer whether to append; it is not a query parameter.
  async ({ isMore: _isMore, ...params }, { dispatch, rejectWithValue }) => {
    const response = await services.getInvoices(params)
    if (!response.ok) {
      dispatch(errorSave(response?.data?.details || unknownError))
      return rejectWithValue(response?.data)
    }
    if (!response.data) return rejectWithValue(undefined)
    return response.data
  }
)

export const exportInvoicesList = createAsyncThunk<Blob, InvoicesListParams>(
  `${sliceName}/exportInvoicesList`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.exportInvoices(params)
    if (!response.ok || !response.data) {
      if (!(response.data instanceof Blob)) {
        dispatch(errorSave(response?.data?.details || unknownError))
      }
      return rejectWithValue(response?.data)
    }
    return response.data
  }
)

export const generateInvoice = createAsyncThunk<
  InvoiceListItem,
  GenerateInvoicePayload
>(`${sliceName}/generateInvoice`, async (payload, { rejectWithValue }) => {
  const response = await services.generateInvoice(payload)
  if (!response.ok || !response.data) {
    // Field errors (over_billed, missing_branding, …) surface to the caller
    // for inline handling; no global toast here.
    return rejectWithValue(response?.data)
  }
  return unwrapEnvelope<InvoiceListItem>(response.data)
})

export const fetchBillableLines = createAsyncThunk<BillableLinesResult, string>(
  `${sliceName}/fetchBillableLines`,
  async (projectId, { dispatch, rejectWithValue }) => {
    const response = await services.getBillableLines(projectId)
    if (!response.ok) {
      dispatch(errorSave(response?.data?.details || unknownError))
      return rejectWithValue(response?.data)
    }
    if (!response.data) return rejectWithValue(undefined)
    const data = unwrapEnvelope<BillableLinesResponse>(response.data)
    return {
      lines: Array.isArray(data) ? data : (data?.lines ?? []),
      default_tax_pct: Array.isArray(data)
        ? null
        : (data?.default_tax_pct ?? null),
      default_retainage_pct: Array.isArray(data)
        ? null
        : (data?.default_retainage_pct ?? null)
    }
  }
)

export const issueInvoice = createAsyncThunk<InvoiceListItem, string>(
  `${sliceName}/issueInvoice`,
  async (id, { rejectWithValue }) => {
    const response = await services.issueInvoice(id)
    if (!response.ok || !response.data) {
      return rejectWithValue(response?.data)
    }
    return unwrapEnvelope<InvoiceListItem>(response.data)
  }
)

export const voidInvoice = createAsyncThunk<InvoiceListItem, string>(
  `${sliceName}/voidInvoice`,
  async (id, { rejectWithValue }) => {
    const response = await services.voidInvoice(id)
    if (!response.ok || !response.data) {
      return rejectWithValue(response?.data)
    }
    return unwrapEnvelope<InvoiceListItem>(response.data)
  }
)

export const sendInvoice = createAsyncThunk<
  SendInvoiceResponse,
  { id: string; recipient?: string }
>(
  `${sliceName}/sendInvoice`,
  async ({ id, recipient }, { rejectWithValue }) => {
    const response = await services.sendInvoice(id, recipient)
    if (!response.ok || !response.data) {
      return rejectWithValue(response?.data)
    }
    return unwrapEnvelope<SendInvoiceResponse>(response.data)
  }
)

export const patchGeneratedInvoice = createAsyncThunk<
  InvoiceListItem,
  { id: string; status?: number } & Partial<GenerateInvoicePayload>
>(
  `${sliceName}/patchGeneratedInvoice`,
  async ({ id, ...data }, { rejectWithValue }) => {
    const response = await services.patchGeneratedInvoice(id, data)
    if (!response.ok || !response.data) {
      return rejectWithValue(response?.data)
    }
    return unwrapEnvelope<InvoiceListItem>(response.data)
  }
)

export const deleteInvoice = createAsyncThunk<string, string>(
  `${sliceName}/deleteInvoice`,
  async (id, { rejectWithValue }) => {
    const response = await services.deleteInvoice(id)
    if (!response.ok) {
      return rejectWithValue(response?.data)
    }
    return id
  }
)

export const fetchInvoiceDeliveries = createAsyncThunk<
  InvoiceDelivery[],
  string
>(`${sliceName}/fetchInvoiceDeliveries`, async (id, { rejectWithValue }) => {
  const response = await services.getInvoiceDeliveries(id)
  if (!response.ok || !response.data) {
    return rejectWithValue(response?.data)
  }
  return unwrapEnvelope<InvoiceDelivery[]>(response.data)
})

export const downloadInvoicePdf = createAsyncThunk<Blob, string>(
  `${sliceName}/downloadInvoicePdf`,
  async (id, { dispatch, rejectWithValue }) => {
    const response = await services.getInvoicePdf(id)
    if (!response.ok || !response.data) {
      if (!(response.data instanceof Blob)) {
        dispatch(errorSave(response?.data?.details || unknownError))
      }
      return rejectWithValue(response?.data)
    }
    return response.data
  }
)
